import React, { FC } from 'react';
import { motion } from 'framer-motion';
import BITEUAPPBANNER from '../../app/assets/images/projectImage/bitEuImages/banner-app.webp'
import BITEULOGO from '../../app/assets/images/projectImage/bitEuImages/bitEuLogo.png'
import BitEuWebContent from '../organisms/ProjectsList/BitEuProject/BitEuWebContent';
import WEB1 from '../../app/assets/images/projectImage/bitEuImages/web-1.webp'
import WEB2 from '../../app/assets/images/projectImage/bitEuImages/web-2.webp'
import BITEUWEB3 from '../../app/assets/images/projectImage/bitEuImages/web-image.webp'
import { getPageData, useData } from '../../app/helper/functions';
import AppsTestimonials from '../molecules/AppsTestimonials/AppsTestimonials';
import CompanyLogoImage from '../molecules/CompanyLogoImage/CompanyLogoImage';
import ContactHelp from '../molecules/ContactHelp/ContactHelp';
import Footer from '../organisms/Footer/Footer';

const BitEuWebPage: FC = () => {
    const { data } = useData();
    const bitEuProject = getPageData(data, 'bitEuProjectPage');
    const { projectOverviewContent, title, description } = bitEuProject?.bitEuProjectDetails;
    
    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };
    
    const slideIn = {
        hidden: { x: -50, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.7 } }
    };
    
    const slideRight = {
        hidden: { x: 50, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.7 } }
    };
    
    const renderDescription = (desc: any) => {
        if (!desc) return null;
        if (typeof desc === 'string') return <>{desc}</>;
        if (Array.isArray(desc)) {
            return (
                <ul className='list-disc ml-6 text-[#e0e0e0] space-y-2'>
                    {desc.map((d: string, i: number) => (
                        <li key={i} className='text-sm md:text-base'>{d}</li>
                    ))}
                </ul>
            )
        }
        // Support object shape: { intro: string, bullets: string[] }
        if (typeof desc === 'object' && (desc.intro || Array.isArray(desc.bullets))) {
            return (
                <div>
                    {desc.intro && <p className='text-[#e0e0e0] mb-3'>{desc.intro}</p>}
                    {Array.isArray(desc.bullets) && (
                        <ul className='list-disc ml-6 text-[#e0e0e0] space-y-2'>
                            {desc.bullets.map((d: string, i: number) => (
                                <li key={i} className='text-sm md:text-base'>{d}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )
        }

        return <>{String(desc)}</>;
    }

    return (
        <div className="bg-[#070707] overflow-x-hidden">
            <div className="pt-28 md:pt-36 lg:pt-52">
                <div className="bg-biteu-app-bg bg-cover bg-center bg-no-repeat">
                    <motion.div 
                        className='flex flex-col md:flex-row gap-6 items-center px-4 md:px-8'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img 
                            variants={slideIn}
                            src={BITEUAPPBANNER} 
                            alt="BITEU App Banner" 
                            className="w-full md:w-[50%] object-cover" 
                        />
                        <motion.div variants={slideRight} className="w-full md:w-auto">
                            <div>
                                <motion.img 
                                    variants={fadeIn} 
                                    src={BITEULOGO} 
                                    alt="BITEU Logo" 
                                    className="w-[60%] sm:w-[40%] md:w-[30%]" 
                                />
                                <motion.div 
                                    variants={fadeIn}
                                    className='text-lg sm:text-xl md:text-2xl w-full md:w-[70%] lg:w-[50%] mt-4 md:mt-0'
                                >
                                    {description}
                                </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>

                    <BitEuWebContent bitEuProject={bitEuProject} />
                    
                    <motion.div 
                        className='flex flex-col md:flex-row items-center justify-center px-4 sm:px-8 md:px-16 xl:px-24 gap-8 md:gap-12 mb-16 md:mb-20 mt-8'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img variants={slideIn} src={WEB1} className='w-full md:w-[40%]' />
                        <motion.div variants={slideRight}>
                            <motion.div variants={fadeIn} className='text-2xl md:text-3xl font-bold text-biteuColor mt-4 md:mt-0'>{projectOverviewContent[0]?.title}</motion.div>
                            <motion.div variants={fadeIn} className='text-[#e0e0e0] pt-2 text-base md:text-lg w-full lg:w-[80%]'>
                                {renderDescription(projectOverviewContent[0]?.description)}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                    
                    <motion.div 
                        className='flex flex-col md:flex-row-reverse items-center justify-center px-4 sm:px-8 md:px-16 xl:px-24 gap-8 md:gap-12 mb-12 md:mb-16'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img variants={slideRight} src={WEB2} className='w-full md:w-[40%]' />
                        <motion.div variants={slideIn} className='flex flex-col justify-start items-start w-full md:w-[40%]'>
                            <motion.div variants={fadeIn} className='text-2xl md:text-3xl font-bold text-biteuColor mt-4 md:mt-0'>{projectOverviewContent[1]?.title}</motion.div>
                            <motion.div variants={fadeIn} className='text-[#e0e0e0] pt-2 text-base md:text-lg'>
                                {renderDescription(projectOverviewContent[1]?.description)}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
                
                <div className='bg-[#070707]'>
                    <motion.div 
                        className='bg-biteuColor px-4 sm:px-8 md:px-16 lg:px-24 mb-16 md:mb-24 lg:mb-32'
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.div 
                            className='relative top-8 sm:top-12 md:top-16 lg:top-24'
                            initial={{ y: 50, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: false, amount: 0.3 }}
                        >
                            <img src={BITEUWEB3} className="w-full" />
                        </motion.div>
                    </motion.div>
                    
                    <motion.div 
                        className='flex flex-col md:flex-row items-center justify-center px-4 sm:px-8 md:px-16 xl:px-24 gap-8 md:gap-12 mb-12 md:mb-16'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img variants={slideIn} src={WEB1} className='w-full md:w-[40%]' />
                        <motion.div variants={slideRight}>
                            <motion.div variants={fadeIn} className='text-2xl md:text-3xl font-bold text-biteuColor mt-4 md:mt-0'>{projectOverviewContent[2]?.title}</motion.div>
                            <motion.div variants={fadeIn} className='text-[#e0e0e0] pt-2 text-base md:text-lg w-full lg:w-[80%]'>
                                {renderDescription(projectOverviewContent[2]?.description)}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                    
                    <motion.div 
                        className='flex flex-col md:flex-row-reverse items-center justify-center px-4 sm:px-8 md:px-16 xl:px-24 gap-8 md:gap-12 mb-12 md:mb-16'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img variants={slideRight} src={WEB2} className='w-full md:w-[40%]' />
                        <motion.div variants={slideIn} className='flex flex-col justify-start items-start w-full md:w-[40%]'>
                            <motion.div variants={fadeIn} className='text-2xl md:text-3xl font-bold text-biteuColor mt-4 md:mt-0'>Features</motion.div>
                            <motion.div variants={fadeIn} className='text-[#e0e0e0] pt-2 text-base md:text-lg'>BIT.EU supports over 350 cryptocurrencies, offering traders a diverse and flexible trading experience. Users can access features like spot trading, futures contracts, and copy trading to match their strategy and skill level. The platform includes multi-language support, making it accessible worldwide, and ensures top-tier security with 2FA and a strict KYC process. With a sleek, responsive interface and powerful backend, BIT.EU delivers fast, seamless trading across web and mobile devices—no matter how active the market gets.</motion.div>
                        </motion.div>
                    </motion.div>
                    
                    {projectOverviewContent[3] && (
                    <motion.div 
                        className='flex flex-col md:flex-row items-center justify-center px-4 sm:px-8 md:px-16 xl:px-24 gap-8 md:gap-12 mb-12 md:mb-16'
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <motion.img variants={slideIn} src={WEB1} className='w-full md:w-[40%]' />
                        <motion.div variants={slideRight} className='flex flex-col justify-start items-start w-full md:w-[40%]'>
                            <motion.div variants={fadeIn} className='text-2xl md:text-3xl font-bold text-biteuColor mt-4 md:mt-0'>{projectOverviewContent[3]?.title}</motion.div>
                            <motion.div variants={fadeIn} className='text-[#e0e0e0] pt-2 text-base md:text-lg'>
                                {renderDescription(projectOverviewContent[3]?.description)}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                    )}
                    
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <div className='flex items-center justify-center my-8'>
                            <button
                                onClick={() => {
                                    const url = bitEuProject?.bitEuProjectDetails?.live_url;
                                    if (url) window.open(url, '_blank');
                                }}
                                className='bg-yellow text-black font-medium rounded-md px-6 py-3 md:px-8 md:py-4 text-lg hover:opacity-90 transition'
                            >
                                View Live Platform →
                            </button>
                        </div>
                        <AppsTestimonials color='#DCCB63' bgcolor={'bg-biteuColor'} textColor='black' activeTextColor='white' />
                    </motion.div>
                    
                    <motion.div 
                        className='p-4 sm:p-6 md:p-8 lg:p-10 px-4 sm:px-12 md:px-24 lg:px-36'
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <CompanyLogoImage />
                    </motion.div>
                    
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.7 }}
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <ContactHelp title="Let's connect & achieve your business goals together" buttonTitle='CONTACT US' />
                    </motion.div>
                    
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default BitEuWebPage;

